import { BooleanGridCell } from 'components/common-form/builder/form/node/field/Grid/cells/BooleanGridCell';
import type { CellMetadata, FieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useMemo, useState } from 'react';

import { EditableGridCell } from './cells/EditableGridCell';
import { SelectableGridCell } from './cells/SelectableGridCell';
import type { CellCoordinates, Navigation } from './GridField.model';

interface GridCellProps {
  isSelected: boolean;
  cellId: string;
  gridId: string;
  isEditing: boolean;
  columnIndex: number;
  rowIndex: number;
  metadata: FieldMetadata;
  value: unknown;
  isDraggable: boolean;
  customizedData?: CellMetadata;
  onValueChanged: (row: number, cellId: string, value: unknown) => void;
  onPaste?: (rowIndex: number, columnIndex: number, values: string[][]) => void;
  onCellSelected: (cellId: string) => void;
  onNavigated: (origin: CellCoordinates, navigation: Navigation, isEditing?: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  tabIndex?: number;
}

export const GridCell: FC<GridCellProps> = ({
  isSelected,
  cellId,
  gridId,
  isEditing,
  rowIndex,
  columnIndex,
  metadata,
  value = null,
  isDraggable,
  onValueChanged,
  onPaste,
  onCellSelected,
  onNavigated,
  setIsEditing,
  tabIndex,
  customizedData,
}) => {
  const { id: columnId } = metadata;

  const [lostInputBeforeEditing, setLostInputBeforeEditing] = useState<KeyboardEventInit | undefined>(undefined);

  const e2e = useMemo<string>(() => `${gridId}-${columnId}-${rowIndex}`, [columnId, gridId, rowIndex]);

  const onEditionStarted = useCallback(
    (lostInput?: KeyboardEventInit) => {
      setIsEditing(true);
      setLostInputBeforeEditing(lostInput);
    },
    [setIsEditing],
  );
  const onEditionFinished = useCallback(() => setIsEditing(false), [setIsEditing]);

  const onNavigatedCallback = useCallback(
    (navigation: Navigation, isEditing?: boolean) => {
      onNavigated({ row: rowIndex, column: columnIndex }, navigation, isEditing);
    },
    [columnIndex, onNavigated, rowIndex],
  );

  const isEditable = isEditing && isSelected && !metadata.isReadOnly && !customizedData?.isReadOnly;
  if (metadata.type === 'CheckboxField' || metadata.type === 'ToggleField') {
    return (
      <BooleanGridCell
        cellId={cellId}
        rowIndex={rowIndex}
        e2e={e2e}
        isSelected={isSelected}
        metadata={metadata}
        onCellSelected={onCellSelected}
        onEditionStarted={onEditionStarted}
        onNavigated={onNavigatedCallback}
        onValueChanged={onValueChanged}
        value={value}
        customizedData={customizedData}
      />
    );
  }
  return isEditable ? (
    <EditableGridCell
      cellId={cellId}
      e2e={e2e}
      columnIndex={columnIndex}
      rowIndex={rowIndex}
      metadata={metadata}
      value={value}
      onValueChanged={onValueChanged}
      onPaste={onPaste}
      onEditionCancelled={onEditionFinished}
      onNavigated={onNavigatedCallback}
      lostInputBeforeEditing={lostInputBeforeEditing}
      tabIndex={tabIndex}
      customizedData={customizedData}
    />
  ) : (
    <SelectableGridCell
      cellId={cellId}
      e2e={e2e}
      isSelected={isSelected}
      metaData={metadata}
      value={value}
      onCellSelected={onCellSelected}
      onEditionStarted={onEditionStarted}
      onNavigated={onNavigatedCallback}
      isDraggable={isDraggable}
      customizedData={customizedData}
    />
  );
};
GridCell.displayName = 'GridCell';
